// utils/complaints.js - Pure logic for the complaint module (C6).
//
// Everything here is free of React and of the network so it can be tested on its own.
// The server stays the authority on a line's status (it recomputes after every save);
// the client mirrors the same rules only to *predict* what a save will do, so the save
// button can say so before the user presses it. The lists themselves (article types,
// complaints, causers ...) always come from GET /api/complaints/taxonomy - nothing
// here hardcodes a choice value.

// ── Roles ─────────────────────────────────────────────────────────────────────
const OFFICE_ROLES = ['Admin', 'Reviewer', 'Supervisor'];
const RD_ROLES = ['R&D', 'R&D Photographer', 'R&D Reviewer'];

/** Who may open the complaint screens at all. R&D never; site users read-only. */
export function canViewComplaints(user) {
  if (!user || RD_ROLES.includes(user.role)) return false;
  return !!user.site_id || OFFICE_ROLES.includes(user.role);
}

/** Office roles without a site: create, edit, reopen, cancel. */
export function canEditComplaints(user) {
  return !!user && !user.site_id && OFFICE_ROLES.includes(user.role);
}

/**
 * Complaints inside the order screen and Search: reviewers, supervisors and admins only,
 * never photographers. The server enforces the same rule; this only decides what to draw.
 */
export function canSeeOrderComplaints(user) {
  return !!user && OFFICE_ROLES.includes(user.role);
}

/** Signal threshold and window are an Admin setting. */
export function canManageComplaintSettings(user) {
  return canEditComplaints(user) && user.role === 'Admin';
}

// ── Fields and statuses ───────────────────────────────────────────────────────
export const PHASE1_FIELDS = ['article_type', 'element_type', 'main_complaint', 'detail'];
export const PHASE2_FIELDS = ['solution', 'causer', 'cost_bearer'];

export const FIELD_LABEL_KEYS = {
  article_type: 'complaints.field.article_type',
  element_type: 'complaints.field.element_type',
  main_complaint: 'complaints.field.main_complaint',
  detail: 'complaints.field.detail',
  description: 'complaints.field.description',
  solution: 'complaints.field.solution',
  causer: 'complaints.field.causer',
  cost_bearer: 'complaints.field.cost_bearer',
};

export const LINE_STATUSES = ['incomplete_complaint', 'pending_verdict', 'incomplete_verdict', 'solved', 'cancelled'];

// Badge class per status, reusing the existing .badge-* vocabulary.
export const STATUS_BADGE = {
  incomplete_complaint: 'badge-warning',
  pending_verdict: 'badge-info',
  incomplete_verdict: 'badge-warning',
  solved: 'badge-success',
  cancelled: 'badge-secondary',
};

const filled = (v) => v !== undefined && v !== null && String(v).trim() !== '';

/** Element types that apply to an article type; empty list = the step is skipped. */
export function elementTypesFor(taxonomy, articleType) {
  if (!taxonomy || !articleType) return [];
  const list = taxonomy.elementTypes?.[articleType];
  return Array.isArray(list) ? list : [];
}

export function detailsFor(taxonomy, mainComplaint) {
  const mc = (taxonomy?.mainComplaints || []).find((m) => m.key === mainComplaint || m.label === mainComplaint);
  return mc ? mc.details || [] : [];
}

/** Required phase 1 fields for this draft (element type only where it applies). */
export function requiredPhase1(line, taxonomy) {
  const needsElement = elementTypesFor(taxonomy, line?.article_type).length > 0;
  return PHASE1_FIELDS.filter((f) => f !== 'element_type' || needsElement);
}

export function missingFields(line, taxonomy) {
  const l = line || {};
  return {
    phase1: requiredPhase1(l, taxonomy).filter((f) => !filled(l[f])),
    phase2: PHASE2_FIELDS.filter((f) => !filled(l[f])),
  };
}

/** Mirror of the server's status rule (contract, "Line status"). */
export function predictStatus(line, taxonomy) {
  if (line?.status === 'cancelled') return 'cancelled';
  const { phase1, phase2 } = missingFields(line, taxonomy);
  if (phase1.length > 0) return 'incomplete_complaint';
  if (phase2.length === PHASE2_FIELDS.length) return 'pending_verdict';
  if (phase2.length > 0) return 'incomplete_verdict';
  return 'solved';
}

export function phaseProgress(line, taxonomy, phase) {
  const l = line || {};
  const fields = phase === 1 ? requiredPhase1(l, taxonomy) : PHASE2_FIELDS;
  return { done: fields.filter((f) => filled(l[f])).length, total: fields.length };
}

/**
 * What the save button promises. `missing` lists the fields that keep the line from
 * the next status - only the ones that matter for the phase the line is in, so a line
 * that is complete in phase 1 is not nagged about a verdict nobody has yet.
 */
export function saveIntent(line, taxonomy) {
  const status = predictStatus(line, taxonomy);
  const { phase1, phase2 } = missingFields(line, taxonomy);
  if (status === 'incomplete_complaint') return { status, complete: false, missing: phase1 };
  if (status === 'incomplete_verdict') return { status, complete: false, missing: phase2 };
  return { status, complete: true, missing: [] };
}

/**
 * Apply one tile choice and keep dependent fields consistent: a new article type drops
 * an element type that no longer fits, a new main complaint drops a detail from the
 * old list, and a causer pre-fills the cost bearer from the cost schema unless the user
 * picked one by hand.
 */
export function applyChoice(draft, field, value, taxonomy) {
  const next = { ...draft, [field]: value };
  if (field === 'article_type') {
    const allowed = elementTypesFor(taxonomy, value);
    if (!allowed.includes(next.element_type)) next.element_type = null;
  }
  if (field === 'main_complaint') {
    if (!detailsFor(taxonomy, value).includes(next.detail)) next.detail = null;
  }
  if (field === 'causer') {
    const causer = (taxonomy?.causers || []).find((c) => c.key === value);
    const prevDefault = (taxonomy?.causers || []).find((c) => c.key === draft.causer)?.defaultCostBearer;
    const untouched = !filled(draft.cost_bearer) || draft.cost_bearer === prevDefault;
    if (causer?.defaultCostBearer && untouched) {
      next.cost_bearer = causer.defaultCostBearer;
      next._costBearerSuggested = true;
    }
  }
  if (field === 'cost_bearer') next._costBearerSuggested = false;
  return next;
}

/** First required step without an answer, phase 1 before phase 2. */
export function nextOpenStep(line, taxonomy) {
  const { phase1, phase2 } = missingFields(line, taxonomy);
  // Walk in display order, not in "missing" order, so element type comes after article type.
  const order = [...requiredPhase1(line || {}, taxonomy), ...PHASE2_FIELDS];
  return order.find((f) => phase1.includes(f) || phase2.includes(f)) || null;
}

/** Fields the user changed, as a PATCH body (cleared values go as null). */
export function changedFields(original, draft) {
  const out = {};
  [...PHASE1_FIELDS, ...PHASE2_FIELDS, 'description'].forEach((f) => {
    const a = filled(original?.[f]) ? original[f] : null;
    const b = filled(draft?.[f]) ? draft[f] : null;
    if (a !== b) out[f] = b;
  });
  return out;
}

/** Order counter: "{solved} of {total - cancelled} solved". Derived, never stored. */
export function orderSummary(lines) {
  const live = (lines || []).filter((l) => l.status !== 'cancelled');
  return { solved: live.filter((l) => l.status === 'solved').length, total: live.length };
}

// ── Labels from the taxonomy ──────────────────────────────────────────────────
export function mainComplaintLabel(taxonomy, value) {
  const mc = (taxonomy?.mainComplaints || []).find((m) => m.key === value);
  return mc ? mc.label : value;
}

export function causerLabel(taxonomy, value) {
  const c = (taxonomy?.causers || []).find((x) => x.key === value);
  return c ? c.label : value;
}

export function valueLabel(taxonomy, field, value) {
  if (!filled(value)) return '';
  if (field === 'main_complaint') return mainComplaintLabel(taxonomy, value);
  if (field === 'causer') return causerLabel(taxonomy, value);
  return value;
}

/** Options for a step as [{value, label}], straight from the taxonomy. */
export function optionsFor(taxonomy, field, line) {
  const t = taxonomy || {};
  const plain = (arr) => (arr || []).map((v) => ({ value: v, label: v }));
  switch (field) {
    case 'article_type': return plain(t.articleTypes);
    case 'element_type': return plain(elementTypesFor(t, line?.article_type));
    case 'main_complaint': return (t.mainComplaints || []).map((m) => ({ value: m.key, label: m.label }));
    case 'detail': return plain(detailsFor(t, line?.main_complaint));
    case 'solution': return plain(t.solutions);
    case 'causer': return (t.causers || []).map((c) => ({ value: c.key, label: c.label }));
    case 'cost_bearer': return plain(t.costBearers);
    default: return [];
  }
}

// ── Dates and list filters ────────────────────────────────────────────────────
const isoDay = (d) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** Default list/report period: the last 30 days (contract decision), "All" off. */
export function defaultComplaintFilters(now = new Date()) {
  const from = new Date(now);
  from.setDate(from.getDate() - 30);
  return { from: isoDay(from), to: isoDay(now), all: false, status: '', q: '', factory: '' };
}

/** Query params for GET /api/complaints. "All" drops the date range. */
export function buildListParams(filters) {
  const f = filters || {};
  const params = {};
  if (f.all) params.all = 1;
  else {
    if (f.from) params.from = f.from;
    if (f.to) params.to = f.to;
  }
  if (f.status) params.status = f.status;
  if (f.q && f.q.trim()) params.q = f.q.trim();
  if (f.factory) params.factory = f.factory;
  return params;
}

/** SQLite datetimes are UTC without a marker; dates-only stay as they are. */
export function parseServerDate(v) {
  if (!v) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return new Date(`${v}T00:00:00`);
  const s = v.includes('T') || v.endsWith('Z') ? v : `${v.replace(' ', 'T')}Z`;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDay(v) {
  const d = parseServerDate(v);
  return d ? d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' }) : '';
}

export function formatMoment(v) {
  const d = parseServerDate(v);
  return d ? d.toLocaleString([], { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
}

/** Lead time in whole days from opened to the latest close; null while open. */
export function leadTimeDays(line) {
  if (line?.lead_time_days != null) return Math.round(line.lead_time_days);
  const a = parseServerDate(line?.opened_at);
  const b = parseServerDate(line?.last_closed_at);
  if (!a || !b) return null;
  return Math.max(0, Math.round((b - a) / 86400000));
}

/**
 * Timeline entries with the briefing's sequential numbering: Closed (1), Reopened (1),
 * Closed (2) ... per line. Sorted oldest first so it reads as the progress of the order.
 */
export function numberEvents(events) {
  const sorted = [...(events || [])].sort((a, b) => {
    const da = parseServerDate(a.at)?.getTime() || 0;
    const db = parseServerDate(b.at)?.getTime() || 0;
    return da - db;
  });
  const counters = {};
  return sorted.map((e) => {
    let data = e.data;
    if (typeof data === 'string') {
      try { data = JSON.parse(data); } catch { data = null; }
    }
    // Reopening a cancelled line undoes the cancel; it is not a numbered reopen.
    const type = e.type === 'reopened' && data?.from === 'cancelled' ? 'uncancelled' : e.type;
    let n = null;
    if (type === 'closed' || type === 'reopened') {
      const k = `${e.line_id || ''}:${type}`;
      counters[k] = (counters[k] || 0) + 1;
      n = counters[k];
    }
    return { ...e, type, n, data: data || null };
  });
}

/** Dot colour per event type, shared by the dossier and the order timeline. */
export const EVENT_DOT_COLOR = {
  closed: 'var(--accent-success)', cancelled: 'var(--text-muted)', reopened: 'var(--accent-warning)',
};

/**
 * The order-level log: the events of all complaint lines in one chronological list, each
 * carrying its line number ("Line 2 - Verdict saved"). `lineId` narrows it to one line.
 * Events that do not belong to a known line (dossier references) are left out.
 */
export function orderTimeline(events, lines, lineId = null) {
  const lineNo = new Map((lines || []).map((l) => [l.id, l.line_no]));
  return numberEvents((events || []).filter((e) => lineNo.has(e.line_id)))
    .filter((e) => !lineId || e.line_id === lineId)
    .map((e) => ({ ...e, line_no: lineNo.get(e.line_id) }));
}

/**
 * Search counter for one order ("1 of 3 solved"), from the fields the order list carries
 * for complaint roles. null when the fields are absent (photographers) or no live line exists.
 */
export function complaintCounter(order) {
  const total = order?.complaint_lines_total;
  if (!total) return null;
  const solved = order.complaint_lines_solved || 0;
  return { solved, total, allSolved: solved === total };
}

/** Adapt dossier photos ({url, thumb_url}) to what PhotoThumb / PhotoViewer read. */
export function toViewerPhoto(p) {
  return { ...p, server_path: p.url, thumb: p.thumb_url || p.url, sync_status: 'synced' };
}
